import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bt, btp } from '../../i18n';
import { Modal } from '../../ui/Modal';
import {
  fetchLibrary,
  removeFromLibrary,
  type LibraryAsset,
  type LibraryPage,
} from '../../lib/assetLibrary';

/**
 * LA BIBLIOTHÈQUE — la première fenêtre que voit la vendeuse quand elle veut une image (feature 0070).
 *
 * Demande de la fondatrice, 05/10/2026 : « si je veux uploader une image dans le builder, la première
 * popup que je vois c'est cette bib des images déjà uploadées, pour que pas à chaque fois je vais
 * télécharger des images, comme font Shopify exactement. »
 *
 * Donc l'ordre des gestes est renversé. AVANT : clic sur la zone → le sélecteur de fichiers du système
 * → la vendeuse retrouve son logo sur son disque, pour la dixième fois. MAINTENANT : clic sur la zone →
 * cette fenêtre, avec ses images déjà envoyées, les plus récentes d'abord ; envoyer un nouveau fichier
 * reste possible, en un bouton, mais ce n'est plus le chemin PAR DÉFAUT.
 *
 * ### Ce que cette fenêtre ne fait pas, et pourquoi
 *  - **Elle n'envoie pas elle-même.** Le bouton « Envoyer » rend le fichier choisi à l'appelant, qui le
 *    confie au registre d'envois en tâche de fond (`lib/mediaUploads.ts`). C'est ce registre qui permet
 *    à un envoi de continuer quand le panneau se ferme — une fenêtre qui porterait l'envoi le perdrait
 *    en se fermant, ce qui est précisément le défaut réparé le 15/09/2026.
 *  - **Le glisser-déposer et le collage restent sur la zone du champ**, pas seulement ici : déposer un
 *    fichier EST déjà un choix explicite, et faire passer ce geste par une fenêtre de sélection serait
 *    ajouter une porte devant une porte ouverte.
 */
export function MediaLibraryModal({
  kind,
  onPick,
  onUploadRequested,
  onClose,
}: {
  /** `IMAGE` pour un champ image, `VIDEO` pour un champ vidéo — on ne montre jamais les deux mélangés. */
  kind: 'IMAGE' | 'VIDEO';
  /** La vendeuse a choisi une entrée existante : l'URL part telle quelle dans le document. */
  onPick: (asset: LibraryAsset) => void;
  /** Elle veut un fichier neuf : l'appelant ouvre le sélecteur et confie l'envoi au registre. */
  onUploadRequested: () => void;
  onClose: () => void;
}) {
  const [pages, setPages] = useState<LibraryPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  /**
   * Incrémenté à chaque recherche : une réponse qui arrive après qu'on a retapé n'écrase plus la liste.
   * Sans ce compteur, taper vite fait clignoter la grille entre deux résultats dans un ordre
   * imprévisible — le genre de défaut qui ne se voit que sur une vraie latence, jamais en local.
   */
  const run = useRef(0);

  const load = useCallback(
    async (needle: string, page: number) => {
      const ticket = ++run.current;
      setLoading(true);
      setFailed(false);
      try {
        const found = await fetchLibrary({ kind, q: needle, page });
        if (ticket !== run.current) return;
        setPages((prev) => (page === 0 ? [found] : [...prev, found]));
      } catch {
        if (ticket !== run.current) return;
        setFailed(true);
      } finally {
        if (ticket === run.current) setLoading(false);
      }
    },
    [kind],
  );

  // La recherche attend que la frappe se calme : un appel par lettre tapée est un appel par lettre
  // tapée, et la bibliothèque d'une boutique bien remplie n'est pas une requête gratuite.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(query, 0), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [query, load]);

  const items = useMemo(() => pages.flatMap((p) => p.items), [pages]);
  const last = pages[pages.length - 1];
  const hasMore = last !== undefined && last.page + 1 < last.totalPages;
  const total = last?.total ?? 0;
  /**
   * Une bibliothèque VIDE et une recherche SANS RÉSULTAT ne sont pas le même écran : la première
   * demande « envoie ta première image », la seconde « essaie un autre mot ». Les confondre, c'est
   * dire à une vendeuse qui a trois cents photos qu'elle n'en a aucune.
   */
  const emptyLibrary = !loading && items.length === 0 && query.trim() === '' && !failed;
  const noMatch = !loading && items.length === 0 && query.trim() !== '' && !failed;

  const chosen = items.find((i) => i.id === selected);

  async function remove(asset: LibraryAsset) {
    setRemoving(asset.id);
    try {
      await removeFromLibrary(asset.id);
      setPages((prev) =>
        prev.map((p) => ({ ...p, items: p.items.filter((i) => i.id !== asset.id) })),
      );
      if (selected === asset.id) setSelected(null);
    } catch {
      setFailed(true);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <Modal titleId="bld-medialib-title" onClose={onClose} wide>
      <div className="bld-medialib">
        <div className="bld-medialib__head">
          <h2 id="bld-medialib-title" className="bld-modal__title">
            {kind === 'VIDEO'
              ? bt('chrome.library.title_video', 'Your videos')
              : bt('chrome.library.title_image', 'Your images')}
          </h2>
          <button type="button" className="bld-btn bld-btn--primary" onClick={onUploadRequested}>
            {bt('chrome.library.upload_new', 'Upload a file')}
          </button>
        </div>

        {/* La recherche reste visible même sur une bibliothèque vide : la faire disparaître déplacerait
            la mise en page sous la main de la vendeuse dès qu'un mot ne trouve rien. */}
        <label className="bld-medialib__search">
          <span className="bld-medialib__search-label">
            {bt('chrome.library.search_label', 'Search by file name')}
          </span>
          <input
            type="search"
            className="bld-tpldlg__input"
            value={query}
            placeholder={bt('chrome.library.search_placeholder', 'File name…')}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
          />
        </label>

        {failed && (
          <p className="bld-medialib__error" role="alert">
            {bt('chrome.library.failed', 'Could not load your files. Please try again.')}
          </p>
        )}

        {emptyLibrary && (
          <div className="bld-medialib__empty">
            <strong>
              {kind === 'VIDEO'
                ? bt('chrome.library.empty_video', 'No videos yet')
                : bt('chrome.library.empty_image', 'No images yet')}
            </strong>
            <span>
              {bt(
                'chrome.library.empty_hint',
                'Every file you upload stays here, ready to reuse anywhere in your store.',
              )}
            </span>
            <button type="button" className="bld-btn bld-btn--primary" onClick={onUploadRequested}>
              {bt('chrome.library.upload_first', 'Upload your first file')}
            </button>
          </div>
        )}

        {noMatch && (
          <p className="bld-medialib__empty-search">
            {btp('chrome.library.no_match', 'No file matches “{q}”.', { q: query.trim() })}
          </p>
        )}

        {items.length > 0 && (
          <>
            {/* « Fichiers : 312 » et non « 312 fichiers » : le `btp` de cet éditeur fait de
                l'interpolation simple, sans règles de pluriel. Une phrase qui accorde son nom au
                nombre serait donc juste en anglais et fausse dans la plupart des sept autres langues
                — l'arabe en compte six formes. La forme à deux-points n'accorde rien. */}
            <p className="bld-medialib__count" role="status">
              {btp('chrome.library.total', 'Files: {n}', { n: total })}
            </p>
            <ul className="bld-medialib__grid">
              {items.map((asset) => {
                const isSelected = asset.id === selected;
                return (
                  <li key={asset.id} className="bld-medialib__cell">
                    <button
                      type="button"
                      className={
                        isSelected ? 'bld-medialib__tile is-selected' : 'bld-medialib__tile'
                      }
                      aria-pressed={isSelected}
                      onClick={() => setSelected(isSelected ? null : asset.id)}
                      onDoubleClick={() => onPick(asset)}
                    >
                      {asset.kind === 'VIDEO' ? (
                        <video
                          className="bld-medialib__thumb"
                          src={asset.url}
                          preload="metadata"
                          muted
                        />
                      ) : (
                        <img
                          className="bld-medialib__thumb"
                          src={asset.url}
                          /* Le texte alternatif de l'image, quand il existe, SINON rien : cette
                             vignette est un bouton qui porte déjà son nom en dessous, et répéter le
                             nom du fichier dans l'alt le ferait annoncer deux fois. */
                          alt={asset.alt ?? ''}
                          loading="lazy"
                        />
                      )}
                      {/* Un nom VIDE est normal : les entrées rattrapées par la reprise n'en ont
                          jamais eu (le backend ne le stockait pas avant 0070). On montre alors la
                          date, qui est la seule chose vraie qu'on sache d'elles. */}
                      <span className="bld-medialib__name">
                        {asset.fileName ||
                          new Date(asset.createdAt).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="bld-iconbtn bld-medialib__remove"
                      disabled={removing === asset.id}
                      aria-label={btp('chrome.library.remove_aria', 'Remove {name} from library', {
                        name: asset.fileName || bt('chrome.library.unnamed', 'this file'),
                      })}
                      onClick={() => void remove(asset)}
                    >
                      {bt('chrome.media.remove', 'Remove')}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {loading && (
          <p className="bld-medialib__loading" role="status">
            {bt('chrome.library.loading', 'Loading your files…')}
          </p>
        )}

        {hasMore && !loading && (
          <button
            type="button"
            className="bld-btn bld-btn--ghost bld-medialib__more"
            onClick={() => void load(query, last.page + 1)}
          >
            {bt('chrome.library.load_more', 'Show more')}
          </button>
        )}

        <div className="bld-modal__actions">
          <button type="button" className="bld-btn bld-btn--ghost" onClick={onClose}>
            {bt('chrome.library.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="bld-btn bld-btn--primary"
            disabled={!chosen}
            onClick={() => chosen && onPick(chosen)}
          >
            {bt('chrome.library.choose', 'Use this file')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
